<template>
  <v-col class="register default-slot" md="7" sm="12">
    <AddCoOwnerModal
      :changeCoOwnerInfo="changeCoOwnerInfo"
      :coOwnerDialog="coOwnerDialog"
      :toggleCoOwnerModal="toggleCoOwnerModal"
      :coOwnerInfo="coOwnerInfo"
    />
    <div class="top-icons">
      <router-link class="back-icon" to="/">
        <font-awesome-icon icon="long-arrow-alt-left" size="lg"/> Back to home
      </router-link>
    </div>
    <h1>QT Number Application</h1>
    <v-alert type="warning">
      This application is for tracking qualifying scores at ASCA events for other/mixed breeds, or Australian Shepherds not eligible for full ASCA registration.
    </v-alert>
    <h2 class="register-title">Owner Information</h2>
    <v-alert type="warning" class="my-3" v-if="registerMember || renewMember">
        <p v-if="registerMember">
          You need to become a member in order to register a dog, you can register <router-link to="/registration/member">here  to proceed with registration.</router-link>.
        </p>
        <p v-else>You're not an active member at the moment please renew <router-link to="/renew/member">here</router-link> to proceed with registration.</p>
    </v-alert>
    <div class="mt-5 d-flex justify-start flex-column align-start">
      <h3>
        <span><b>ASCA Member ID: </b></span>
        <span>{{ownerInfo.memberId}}</span>
      </h3>
      <h3 class="mt-3">
        <span><b>Name: </b></span>
        <span>{{ownerInfo.name}}</span>
      </h3>
      <h3 class="mt-3">
        <span><b>Email: </b></span>
        <span>{{ownerInfo.email}}</span>
      </h3>
      <h3 class="mt-3">
        <span><b>Phone Number: </b></span>
        <span>{{ownerInfo.phoneNumber}}</span>
      </h3>
      <h3 class="mt-3 d-flex flex-column">
        <span class="align-self-start"><b>Address: </b></span>
        <span class="qt-address">
          {{ownerInfo.address.address}} <br v-if="!!ownerInfo.address.address" />
          {{ownerInfo.address.international}} <br v-if="!!ownerInfo.address.international" />
          {{addComma(ownerInfo.address.city, ownerInfo.address.stateProvince)}} {{addComma(ownerInfo.address.stateProvince, ownerInfo.address.zipPostal)}} {{ownerInfo.address.zipPostal}}
          <br v-if="!!ownerInfo.address.city || !!ownerInfo.address.stateProvince || !!ownerInfo.address.zipPostal" />
          {{ownerInfo.address.country}}
        </span>
      </h3>
      <h3 v-if="isUnder18()" class="mt-3">
        <span><b>Date Of Birth: </b></span>
        <span>{{$dateUtils.formatLongDate(userInfo.birthDate)}}</span>
      </h3>
    </div>
    <div v-if="coOwnerInfo.length" class="mt-5 d-flex align-start flex-column">
      <h3>
        <b>Co-Owners:</b>
        <v-badge
          v-if="coOwnerInfo.length > 0"
          :content="coOwnerInfo.length"
          offset-y="-5"
        />
      </h3>
      <div v-for="(owner, idx) in coOwnerInfo" :key="owner.memberId">
        <span>
          {{owner.firstName}} {{owner.lastName}}
          <span v-if="!!owner.memberId">
            (Member: {{owner.memberId}})
          </span>
        </span>
        <a v-if="idx === coOwnerInfo.length - 1" v-on:click="toggleCoOwnerModal()">Edit</a>
      </div>
    </div>
    <div v-else class="mt-5 d-flex align-start flex-column">
      <v-btn
        class="white--text"
        large
        color="primary"
        :dark="!registerMember && !renewMember"
        :disabled="registerMember || renewMember"
        v-on:click="toggleCoOwnerModal()"
      >ADD CO-OWNER</v-btn>
    </div>
    <div class="mt-10 mr-5">
      <p class="signature-terms">
        Upon signing, I testify that all data submitted is correct.
        I agree to abide by the <ASCAArtIncLink />, <BylawsLink />, rules of the ASCA Registries and all event programs, and to disciplinary action, if necessary, as determined by the Board of Directors of ASCA.
      </p>
      <div class="agreement-container">
        <div class="agreement-input-container">
            <div class="agreement-input">
                <p>X</p>
                <v-text-field
                  flat
                  solo
                  label="Owner's Digital Signature Here"
                  type="text"
                  v-model="ownerInfo.signature"
                  hide-details="auto"
                />
            </div>
            <p class="agreement-date">{{$dateUtils.formatLongDate(agreementDate)}}</p>
        </div>
    </div>
    </div>
    <v-btn
      class="white--text"
      large
      color="primary"
      :dark="isValid()"
      :disabled="!isValid()"
      v-on:click="changePage(2)"
    >NEXT</v-btn>
    <p class="steps">STEP <b>1</b> OF <b>3</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";

import juniorHelpers from "@/helpers/validate-junior-age";

import { UserInfo } from "@/types/User";

import AddCoOwnerModal from "@/components/Portal/AddCoOwnerModal_QTDog.vue";
import FooterText from "@/components/common/FooterText.vue";
import ASCAArtIncLink from "@/components/common/ASCAArtIncLink.vue";
import BylawsLink from "@/components/common/BylawsLink.vue";

export default Vue.extend({
  name: "OwnerInformation",
  components: { FooterText, AddCoOwnerModal, ASCAArtIncLink, BylawsLink },
  props: {
    changePage: Function,
    changeCoOwnerInfo: Function,
    registerMember: Boolean,
    renewMember: Boolean,
    ownerInfo: Object,
    coOwnerInfo: Array,
  },
  data: function () {
    return {
      agreementDate: new Date(),
      coOwnerDialog: false,
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
  methods: {
    toggleCoOwnerModal: function () {
      this.$data.coOwnerDialog = !this.$data.coOwnerDialog;
    },
    isValid() {
      return !this.registerMember && !this.renewMember && !!this.ownerInfo.signature;
    },
    isUnder18() {
      return this.userInfo != null
        && this.userInfo.birthDate != null
        && juniorHelpers.isUnder18(this.userInfo.birthDate);
    },
    addComma(targetValue: string | null, afterValue: string | null) {
      if (!targetValue) return '';
      else if (!!afterValue) return `${targetValue},`;
      else return targetValue;
    }
  }
});
</script>